import React, { useContext, useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router";
import posthog from "posthog-js";
import { useFeatureFlagEnabled } from "posthog-js/react";
import { GPUInstanceType } from "components/Environment/Settings/Tabs/Compute/InstanceChanger/GPUTypes";
import { OrgContext } from "contexts/OrgContext";
import { OneClickDeployContext } from "contexts/OneClickDeployContext";
import { useLaunchable } from "../../utils";
import { getLaunchableQueryParams } from "../shared/utils";
import { DeployPageContent } from "../shared/DeployPageContent";
import { isKasAuthFlow } from "server/kas/utils";

const WillHillLaunchableIDMessUp = "env-2hQX3n7ae5mq3NjNZ32DfAG0tJf";

const LaunchableDeploy: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const { queryLaunchableID } = getLaunchableQueryParams(location.search);
  const { launchableData, error, loading, launchableNotFound } = useLaunchable(
    queryLaunchableID || ""
  );

  const orgContext = useContext(OrgContext);
  const [nameErrorMessage, setErrorMessage] = useState<string>("");
  const [instanceObject, setInstanceObject] = useState<GPUInstanceType | null>(
    null
  );
  const [deploymentStarted, setDeploymentStarted] = useState(false);
  const oneClickContext = useContext(OneClickDeployContext);
  const flagNimsHackathonEnabled = useFeatureFlagEnabled("nims-hackathon");

  const [extraEventProperties, setExtraEventProperties] = useState<
    Record<string, any>
  >({});

  useEffect(() => {
    if (!isKasAuthFlow) {
      const redirectUrl = `/launchable-migrate-notice?${new URLSearchParams({
        redirect: `https://brev.nvidia.com/launchable/deploy/now?${query.toString()}`,
      }).toString()}`;
      history.push(redirectUrl);
    }
  }, [query, history]);

  useEffect(() => {
    const fn = async () => {
      const eventProperties = {
        launchableID: queryLaunchableID,
        launchableInstanceType: launchableData?.instanceType,
        workspaceGroupID: launchableData?.workspaceGroupId,
        launchableCreatedByUserID: launchableData?.userId,
        launchableCreatedByOrgID: launchableData?.orgId,
        launchableRawURL: location.pathname + location.search,
      };
      setExtraEventProperties(eventProperties);
      posthog.capture("Launchable Viewed", eventProperties);
    };
    if (!!launchableData) {
      fn();
    }
  }, [launchableData]);

  const isAllowedBaseImage = useMemo(() => {
    if (!launchableData?.containerSelected.verbBuild?.containerUrl) {
      return true;
    }

    if (
      !flagNimsHackathonEnabled &&
      launchableData?.containerSelected?.verbBuild?.containerUrl ===
        "nvcr.io/mphexwv2ysej/meta-llama3-8b-instruct:24.05.rc7"
    ) {
      return false;
    }

    return true;
  }, [flagNimsHackathonEnabled, launchableData?.containerSelected]);

  const handleDeploy = () => {
    if (queryLaunchableID === WillHillLaunchableIDMessUp) {
      if (launchableData) {
        launchableData?.containerSelected?.verbBuild?.containerUrl ===
          "nvcr.io/nvidia/tensorrt:24.05-py3";
      }
    }
    setDeploymentStarted(true);
    oneClickContext.startDeploymentFromLaunchable(
      launchableData,
      extraEventProperties
    );
    posthog.capture("Launchable Deployed", extraEventProperties);
  };

  return (
    <DeployPageContent
      loading={loading}
      launchableNotFound={launchableNotFound}
      launchableData={launchableData}
      instanceObject={instanceObject}
      setInstanceObject={setInstanceObject}
      nameErrorMessage={nameErrorMessage}
      isAllowedBaseImage={isAllowedBaseImage}
      deploymentStarted={deploymentStarted}
      isInstancesLoading={orgContext.isInstancesLoading}
      allInstancesAvailable={orgContext.allInstancesAvailable}
      isDeploymentFinished={oneClickContext.isFinished}
      onDeploy={handleDeploy}
      workspaceId={oneClickContext.environment?.workspaceId}
      activeOrgId={orgContext.activeOrgId}
    />
  );
};

export default LaunchableDeploy;
